import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

type UsersContextType = {
    users: any[];
    activeUsers: any[];  // New field for active users
    refreshUsers: () => Promise<void>;
};

const UsersContext = createContext<UsersContextType | undefined>(undefined);

export const useUsers = () => {
    const context = useContext(UsersContext);
    if (!context) {
        throw new Error('useUsers must be used within a UsersProvider');
    }
    return context;
};

// New hook specifically for active users
export const useActiveUsers = () => {
    const context = useContext(UsersContext);
    if (!context) {
        throw new Error('useActiveUsers must be used within a UsersProvider');
    }
    return { users: context.activeUsers };
};

export const UsersProvider = ({ children }: any) => {
    const [users, setUsers] = useState<any[]>([]);
    const session = useSession();

    // Calculate active users using useMemo
    const activeUsers = useMemo(() => {
        return users.filter(user => {
            const public_metadata = user.public_metadata || {};
            return !public_metadata.archived && public_metadata.internal !== false;
        });
    }, [users]);

    const refreshUsers = useCallback(async () => {
        if (session.isLoaded && session.isSignedIn) {
            try {
                const response = await fetchWithAuth(`${API_URL}/clerk/all`, { method: 'GET' }, session.session);
                const data = await response.json();
                if (data && data.length > 0) {
                    setUsers(data);
                } else {
                    console.error("Error fetching users: Empty data.");
                }
            } catch (error) {
                console.error("There was an error fetching the users:", error);
            }
        }
    }, [session.isLoaded, session.isSignedIn, session.session]);

    useEffect(() => {
        refreshUsers();
    }, [refreshUsers]);

    // MEMOIZE: Context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        users,
        activeUsers,
        refreshUsers
    }), [users, activeUsers, refreshUsers]);

    return (
        <UsersContext.Provider value={contextValue}>
            {children}
        </UsersContext.Provider>
    );
};